import FormSection from "../../../common/components/functional/formSection/FormSection";
import { useContext, useEffect, useMemo, useState } from "react";
import { AccessRightContext } from "../../user/AccessRightProvider";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  selectTalentById,
  setTalentWorkingArea,
} from "../../talents/talentsSlice";
import { Talent, talentIsDeactivated } from "../../talents/talentAPI";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { TalentWorkingAreaForm } from "../../talents/types/common";
import { isFulfilled } from "@reduxjs/toolkit";
import { Input } from "../editForms/salaries/CustomInputs";
import { NumberFormatValues, NumericFormat } from "react-number-format";

const formatDefaultValue = (value?: number) => {
  if (!value) return "";
  const toKm = value / 1000;
  return toKm.toString();
};

const isAllowed = ({ floatValue, value }: NumberFormatValues): boolean => {
  if (value === "" || value === undefined) return true;
  if (floatValue === undefined || Number.isNaN(floatValue)) return false;
  return floatValue > 0 && floatValue <= 1000;
};

const TalentWorkingArea = () => {
  const [edit, setEdit] = useState<boolean>(false);

  const { t } = useTranslation(["common", "talentProfile"]);
  const { canEditTalentProfile } = useContext(AccessRightContext);

  const { talentId } = useParams();
  const dispatch = useAppDispatch();

  const { status } = useAppSelector((state) => state.talents);
  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );

  const isLoading = status === "pending";

  const defaultValues = useMemo(
    () => ({
      radiusInMeters: formatDefaultValue(
        talent?.workingAreas?.config?.distanceFromHome,
      ),
    }),
    [talent?.workingAreas?.config?.distanceFromHome],
  );

  const methods = useForm<TalentWorkingAreaForm>({
    defaultValues,
    mode: "onChange",
  });

  const {
    handleSubmit,
    control,
    formState: { isDirty },
    reset,
  } = methods;

  // Reset form values when workingArea config changes
  useEffect(() => {
    reset(defaultValues);
  }, [talent?.workingAreas?.config?.distanceFromHome, reset, defaultValues]);

  const onSubmit = (data: TalentWorkingAreaForm) => {
    const payload = {
      talentId: talentId!,
      version: talent?.workingAreas?.config?.version || 0,
      ...(data.radiusInMeters && {
        radiusInMeters: parseInt(data.radiusInMeters) * 1000,
      }),
    };
    dispatch(setTalentWorkingArea(payload)).then((action) => {
      if (isFulfilled(action)) {
        setEdit(false);
      }
    });
  };

  const onEdit = () => {
    if (!edit) setEdit(true);
    if (edit) return handleSubmit(onSubmit)();
  };

  const onCancel = () => {
    setEdit(false);
    reset(defaultValues);
  };

  const formatValue = (value?: string) => {
    if (value) {
      return isLoading ? "" : `${value} ${t("km")}`;
    }

    // empty state
    return t("notSpecified");
  };

  return (
    <FormSection
      title={t("talentProfile:workingArea")}
      onEdit={onEdit}
      onCancel={onCancel}
      saveDisabled={!isDirty}
      edit={edit}
      readOnly={!canEditTalentProfile || talentIsDeactivated(talent)}
    >
      <Grid
        item
        container
        alignItems="center"
        xs={3}
        justifyContent="flex-end"
        sx={{ minHeight: "48px" }}
      >
        <Typography textAlign="end" variant="secondary">
          {t("area")}
        </Typography>
      </Grid>
      <Grid
        item
        container
        xs={7}
        alignItems="center"
        sx={{ minHeight: "48px" }}
      >
        <Controller
          name="radiusInMeters"
          control={control}
          render={({ field: { name, value, onChange } }) =>
            edit ? (
              <NumericFormat
                name={name}
                disabled={!edit}
                isAllowed={(values) => isAllowed(values)}
                value={value}
                valueIsNumericString
                onValueChange={(values) => {
                  onChange(values.value);
                }}
                maxwidth="96px"
                customInput={Input}
                decimalScale={0}
                unit={t("km")}
                allowNegative={false}
              />
            ) : (
              <Typography>{formatValue(value)}</Typography>
            )
          }
        />
      </Grid>
    </FormSection>
  );
};

export default TalentWorkingArea;
